// KiraActionModals.jsx — single mount-point for all action modals/drawers
// triggered by K.I.R.A.  Used by KiraModal (the floating chat) so AI-driven
// actions (apply leave, raise ticket, etc.) still surface real HR forms even
// after AI chat was removed from /chat.
//
// Action names are the orchestrator's `type` field — they map 1:1 to a modal
// or drawer.  Use `isModalAction(type)` to gate inline-form vs modal rendering.
import React from "react";
import {
  ChatLeaveModal,
  ChatRegularisationModal,
  ChatTicketModal,
  ChatCorrectionModal,
  ChatSelfReviewModal,
  ChatIncomeRequestModal,
  ChatSalaryBreakdownModal,
  ChatTicketDrawer,
  ChatLeaveApprovalDrawer,
  ChatEmployeePayDrawer,
  ChatReviewDrawer,
  ChatCreateUserModal,
  ChatMemberAttendanceDrawer,
  ChatMemberLeaveDrawer,
  ChatTaskCreateModal,
  ChatProjectCreateModal,
} from "../chat/ChatModals";

const MODAL_ACTIONS = new Set([
  "leave_apply",
  "attendance_regularise",
  "ticket_raise",
  "payslip_correction",
  "self_review",
  "income_certificate",
  "salary_breakdown",
  "ticket_manage",
  "leave_approval",
  "employee_pay",
  "performance_review",
  "create_user",
  "member_attendance",
  "member_leave",
  "task_create",
  "project_create",
]);

export function isModalAction(action) {
  return MODAL_ACTIONS.has(action);
}

export default function KiraActionModals({ activeModal, modalData, onClose, onSuccess }) {
  if (!activeModal || !MODAL_ACTIONS.has(activeModal)) return null;
  const data = modalData || {};
  const today = () => new Date().toISOString().split("T")[0];

  return (
    <div data-kira-modal="true">
      {activeModal === "leave_apply" && (
        <ChatLeaveModal open onClose={onClose} onSuccess={onSuccess} />
      )}

      {activeModal === "attendance_regularise" && (
        <ChatRegularisationModal
          open
          onClose={onClose}
          onSuccess={onSuccess}
          date={data.date || today()}
        />
      )}

      {activeModal === "ticket_raise" && (
        <ChatTicketModal open onClose={onClose} onSuccess={onSuccess} />
      )}

      {activeModal === "payslip_correction" && (
        <ChatCorrectionModal open onClose={onClose} onSuccess={onSuccess} />
      )}

      {activeModal === "self_review" && (
        <ChatSelfReviewModal open onClose={onClose} onSuccess={onSuccess} />
      )}

      {activeModal === "income_certificate" && (
        <ChatIncomeRequestModal open onClose={onClose} onSuccess={onSuccess} />
      )}

      {activeModal === "salary_breakdown" && (
        <ChatSalaryBreakdownModal open onClose={onClose} />
      )}

      {activeModal === "ticket_manage" && (
        <ChatTicketDrawer
          open
          onClose={onClose}
          onSuccess={onSuccess}
          ticketId={data.ticket_id}
          ticket={data.ticket || null}
        />
      )}

      {activeModal === "leave_approval" && (
        <ChatLeaveApprovalDrawer
          open
          onClose={onClose}
          onSuccess={onSuccess}
          requestId={data.request_id}
          request={data.request || null}
        />
      )}

      {activeModal === "employee_pay" && (
        <ChatEmployeePayDrawer
          open
          onClose={onClose}
          onSuccess={onSuccess}
          employeeId={data.employee_id}
          row={data.row || null}
        />
      )}

      {activeModal === "performance_review" && (
        <ChatReviewDrawer
          open
          onClose={onClose}
          onSuccess={onSuccess}
          employeeId={data.employee_id}
          row={data.row || null}
        />
      )}

      {activeModal === "create_user" && (
        <ChatCreateUserModal open onClose={onClose} onSuccess={onSuccess} />
      )}

      {activeModal === "member_attendance" && (
        <ChatMemberAttendanceDrawer
          open
          onClose={onClose}
          onSuccess={onSuccess}
          employeeId={data.employee_id}
          member={data.member || null}
        />
      )}

      {activeModal === "member_leave" && (
        <ChatMemberLeaveDrawer
          open
          onClose={onClose}
          onSuccess={onSuccess}
          employeeId={data.employee_id}
          member={data.member || null}
        />
      )}

      {activeModal === "task_create" && (
        <ChatTaskCreateModal open onClose={onClose} onSuccess={onSuccess} />
      )}

      {activeModal === "project_create" && (
        <ChatProjectCreateModal open onClose={onClose} onSuccess={onSuccess} />
      )}
    </div>
  );
}
